/**
 * A moon as a disc, drawn in the shape the server sends: an outline for a new
 * moon, filled for a full one, and for any other night the lit part, from how
 * much is lit and on which side (B91, Today's Almanac).
 *
 * Shared by the Seasons page's Recent and coming up and Today's Almanac, so a
 * moon looks the same wherever it is named. It used to be one icon for both
 * phases, which made a new moon and a full moon impossible to tell apart.
 * The shape, the lit share and the side are the server's (services/today/
 * skyAhead: full or new only on the night it is seen, the side already
 * flipped south of the equator); the disc only draws them, deciding nothing.
 * Drawn in the current text colour, so the caller's theme class colours it.
 *
 * Used by: Today's Almanac (TodayAlmanac) and the Seasons header
 * (SeasonHeader).
 *
 * @module MoonDisc
 */

import React from 'react';

/** The shape to draw, and its size and colour from the caller. */
interface MoonDiscProps {
  /** As the server sends it: new draws the outline, full fills it, part draws the lit part (lit, litSide). */
  shape: 'new' | 'full' | 'part';
  /** How much is lit, 0 to 1, as the server sends it (for a part). */
  lit?: number;
  /** The lit side as the gardener sees it, as the server sends it (for a part). */
  litSide?: 'left' | 'right';
  /** Size and colour; the disc is drawn in the text colour. */
  className?: string;
}

/** The disc's centre, across, in its 16-unit box. */
const CX = 8;
/** The disc's centre, down. */
const CY = 8;
/** The disc's radius. */
const R = 6;

/**
 * The lit part's outline: the lit half's edge, then the terminator, an ellipse whose width follows the share (a
 * crescent under half, a gibbous over), drawn lit on the right; the caller mirrors it for the left.
 *
 * @param lit - The lit share, 0 to 1.
 * @returns The SVG path.
 */
function litPath(lit: number): string {
  const rx = Math.abs(1 - 2 * lit) * R;
  const sweep = lit > 0.5 ? 1 : 0;
  return `M${CX} ${CY - R} A${R} ${R} 0 0 1 ${CX} ${CY + R} A${rx} ${R} 0 0 ${sweep} ${CX} ${CY - R}Z`;
}

/** The disc, in the current text colour. */
export const MoonDisc: React.FC<MoonDiscProps> = ({ shape, lit, litSide, className }) => (
  <svg viewBox="0 0 16 16" className={className} aria-hidden="true">
    {shape === 'part' && typeof lit === 'number' && (
      <path d={litPath(lit)} fill="currentColor" transform={litSide === 'left' ? 'translate(16 0) scale(-1 1)' : undefined} />
    )}
    <circle cx={CX} cy={CY} r={R} fill={shape === 'full' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" />
  </svg>
);
